/**
 * Die Anrede-Auswahl (`tenant-create-salutation` / `tenant-edit-salutation`,
 * Spec 03, „Die Anrede"). Eine Reihe aus drei Schaltflächen statt einer
 * Klappliste — bei drei Werten in einem Griff erreichbar, und mit `←`/`→`
 * bedienbar wie eine Radiogruppe (Spec 03, „Tastatur").
 *
 * Die Optionen kommen von `GET /tenants/salutations` — der Client pflegt
 * keine eigene Zuordnung von Werten auf deutsche Bezeichnungen (I9).
 *
 * **Ohne Vorbelegung** (AK-201): `value` ist `''`, solange nichts gewählt
 * ist. Ein viertes, stummes Segment „—" erlaubt das Zurücknehmen einer
 * Auswahl — dieselbe Rolle wie das vierte iOS-Segment „keine Anrede", das
 * `null` sendet (Spec 03, „iOS"); die Spec beschreibt es dort wörtlich, für
 * Web lässt sie die Umsetzung offen, dieselbe neutrale Form schien hier
 * naheliegender als sie zu erfinden.
 */

import { useSalutations } from '@/features/tenants/api'
import { FIELD_CLASS } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'

interface SalutationFieldProps {
  readonly testId: string
  readonly errorPrefix: string
  /** Serverwert (`herr`/`frau`/`eheleute`) oder `''` für „keine Anrede". */
  readonly value: string
  readonly onChange: (value: string) => void
  readonly error: FieldError | undefined
  readonly disabled?: boolean
}

const API_FIELD = 'salutation'
const NONE_TOKEN = 'keine'

export function SalutationField({ testId, errorPrefix, value, onChange, error, disabled = false }: SalutationFieldProps) {
  const salutationsQuery = useSalutations()
  const hasError = error?.field === API_FIELD
  const errorTestId = `${errorPrefix}-error-${API_FIELD}`

  const options = salutationsQuery.data ?? []
  // `null` steht für „keine Anrede" — ein eigener Eintrag, kein vierter
  // Serverwert (die drei echten kommen ausschließlich vom Server, I9).
  const entries: { readonly token: string; readonly value: string; readonly label: string }[] = [
    ...options.map((option) => ({ token: option.value, value: option.value, label: option.label })),
    { token: NONE_TOKEN, value: '', label: '—' },
  ]
  // Rovender Tabstopp: genau ein Segment ist per `Tab` erreichbar — das
  // gewählte, sonst das erste (Standardverhalten einer Radiogruppe).
  const activeIndex = Math.max(
    entries.findIndex((entry) => entry.value === value),
    0,
  )

  function focusSibling(currentIndex: number, delta: number) {
    const next = entries[(currentIndex + delta + entries.length) % entries.length]
    if (next === undefined) return
    document.getElementById(`${testId}-${next.token}`)?.focus()
  }

  return (
    <div className="flex flex-col gap-1">
      <span id={`${testId}-label`} className="text-label text-muted">
        Anrede
      </span>
      <div
        role="radiogroup"
        aria-labelledby={`${testId}-label`}
        aria-invalid={hasError}
        aria-describedby={hasError ? errorTestId : undefined}
        data-testid={testId}
        className="flex gap-1"
      >
        {entries.map((entry, index) => {
          const isChecked = value === entry.value
          return (
            <button
              key={entry.token}
              id={`${testId}-${entry.token}`}
              type="button"
              role="radio"
              aria-checked={isChecked}
              data-testid={`${testId}-${entry.token}`}
              disabled={disabled || salutationsQuery.isPending}
              tabIndex={index === activeIndex ? 0 : -1}
              onClick={() => {
                onChange(entry.value)
              }}
              onKeyDown={(event) => {
                if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                  event.preventDefault()
                  focusSibling(index, 1)
                } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                  event.preventDefault()
                  focusSibling(index, -1)
                }
              }}
              className={`text-text flex-1 px-3 ${FIELD_CLASS} ${
                isChecked ? 'border-accent bg-accent/10' : ''
              }`}
            >
              {entry.label}
            </button>
          )
        })}
      </div>
      {hasError && (
        <p id={errorTestId} data-testid={errorTestId} role="alert" className="text-status-overdue">
          {error.message}
        </p>
      )}
    </div>
  )
}
